import type { AccessState } from '@/access/access';
import type { CheckoutConflictHint, PlanSlug, User, UserSubscription } from '@/api/types';
import { paidSubscriptionOf } from '@/auth/mapUser';
import { intlLocale, t } from '@/i18n/i18n';

export { paidSubscriptionOf };

/**
 * Состояние показанной строки подписки (BILL-7):
 * free · active · grace (отменена, доступ до конца периода) · past_due (списание
 * не прошло, идёт дунинг Paddle) · paused (пауза в Paddle: ни списаний, ни доступа).
 */
export type BillingKind = 'free' | 'active' | 'grace' | 'past_due' | 'paused';

export interface BillingState {
  /** Состояние строки «Текущий план» — того, что даёт доступ сейчас (или платной строки без доступа). */
  kind: BillingKind;
  plan: PlanSlug | null;
  expiresAt: string | null;
  cancelledAt: string | null;
  /** Показанная строка заведена в Paddle — доступны портал, смена тарифа и resume. Иначе активирована админом (BILL-7). */
  paddleManaged: boolean;
  /** Живая платная строка Paddle (active · past_due · paused) — и когда доступ даёт грант. null — платной подписки нет. */
  paid: UserSubscription | null;
  /** Платная строка есть, а «Текущий план» показывает другую (грант): управление ею — отдельной секцией. */
  paidMasked: boolean;
  /** Списание не прошло: Paddle повторяет попытки и ждёт от нас заметный сигнал «обнови карту». */
  paymentIssue: boolean;
  /** Есть клиент Paddle — Customer Portal (счета, карта) доступен и без живой подписки. */
  hasPaddleCustomer: boolean;
}

function planSlugOf(row: UserSubscription | null): PlanSlug | null {
  const slug = row?.plan?.slug;
  return slug === 'monthly' || slug === 'yearly' ? slug : null;
}

/** Состояние платной строки Paddle самой по себе — для секции «Paid subscription» и бейджей. */
export function paidRowKind(row: UserSubscription): Exclude<BillingKind, 'free'> {
  if (row.status === 'paused') return 'paused';
  if (row.paddle_status === 'past_due') return 'past_due';
  return row.cancelled_at ? 'grace' : 'active';
}

/**
 * Страница биллинга смотрит на две строки из /me: `subscription` (доступ: грант или
 * оплата с самым поздним сроком) и `paddle_subscription` (платная строка Paddle,
 * даже когда доступ даёт грант). Показанная карточка — строка доступа; без неё —
 * платная строка, если она на паузе или в дунинге; иначе — free.
 */
export function resolveBillingState(user: User | null): BillingState {
  const paid = paidSubscriptionOf(user);
  const sub = user?.has_subscription && user.subscription ? user.subscription : null;
  const paymentIssue = paid?.paddle_status === 'past_due';
  const hasPaddleCustomer = !!user?.has_paddle_customer || !!paid;
  const base = { paid, paymentIssue, hasPaddleCustomer };

  if (sub) {
    // Показанная строка и есть платная? Иначе платная (если есть) замаскирована грантом.
    const shownIsPaid = !!paid && sub.paddle_subscription_id === paid.paddle_subscription_id;
    return {
      ...base,
      kind: shownIsPaid && paymentIssue ? 'past_due' : sub.cancelled_at ? 'grace' : 'active',
      plan: planSlugOf(sub),
      expiresAt: sub.expires_at ?? null,
      cancelledAt: sub.cancelled_at ?? null,
      paddleManaged: !!sub.paddle_subscription_id,
      paidMasked: !!paid && !shownIsPaid,
    };
  }

  // Доступа нет, но Paddle ещё может списать: пауза сама возобновится в resume_at,
  // past_due с прошедшим периодом добирает дунинг — вместо Subscribe показываем строку.
  const paidKind = paid ? paidRowKind(paid) : null;
  if (paid && (paidKind === 'paused' || paidKind === 'past_due')) {
    return {
      ...base,
      kind: paidKind,
      plan: planSlugOf(paid),
      expiresAt: paid.expires_at ?? null,
      cancelledAt: paid.cancelled_at ?? null,
      paddleManaged: true,
      paidMasked: false,
    };
  }

  return {
    ...base,
    kind: 'free',
    plan: null,
    expiresAt: null,
    cancelledAt: null,
    paddleManaged: false,
    // Платная строка жива, а доступа нет (вебхук продления ещё не дошёл): покупка дала бы дубль.
    paidMasked: !!paid,
  };
}

/**
 * Подсказка guard'а 409 `subscription_exists` по живой платной строке из /me — та же
 * логика, что у бэкенда (`SubscriptionCheckoutController::checkout`): окно оплаты по
 * ней не рисует Subscribe вовсе, не дожидаясь 409 от сервера.
 * null — живой платной строки нет, покупать можно.
 */
export function checkoutConflictHintFor(
  row: UserSubscription | null | undefined,
): CheckoutConflictHint | null {
  if (!row) return null;
  if (row.status === 'paused') return 'paused';
  if (row.status !== 'active') return null;
  if (row.paddle_status === 'past_due') return 'past_due';
  if (row.cancelled_at) return 'resume';
  return 'change_plan';
}

/**
 * Где показывается текст: на странице биллинга кнопки Resume и «Сменить тариф»
 * стоят прямо под ним, в окне оплаты их нет — там отправляем на страницу биллинга.
 */
export type BillingSurface = 'billing' | 'modal';

/** Подсказка guard'а 409 → что делать вместо новой покупки. Сырой код `subscription_exists` покупателю не показывается. */
export function describeCheckoutConflict(
  hint: string | undefined,
  surface: BillingSurface = 'modal',
): string {
  const onBilling = surface === 'billing';
  switch (hint) {
    case 'resume':
      return t(onBilling ? 'paywall.conflict.resumeBilling' : 'paywall.conflict.resume');
    case 'change_plan':
      return t(onBilling ? 'paywall.conflict.changePlanBilling' : 'paywall.conflict.changePlan');
    case 'paused':
      return t('paywall.conflict.paused');
    case 'past_due':
      return t('paywall.conflict.pastDue');
    case 'already_subscribed':
    default:
      return t('paywall.conflict.alreadySubscribed');
  }
}

export type IntentDecision = 'none' | 'wait' | 'drop' | 'billing' | 'checkout';

/**
 * Судьба намерения `?action=subscribe&plan=` (BILL-3): ждём подтверждённый доступ,
 * логин и код; в бесплатном режиме стираем; при подписке или живой платной строке
 * (пауза, дунинг) ведём в Billing — второй чекаут дал бы двойное списание.
 */
export function decideSubscribeIntent(input: {
  pending: PlanSlug | null;
  access: AccessState;
  user: User | null;
  isAuthenticated: boolean;
}): IntentDecision {
  const { pending, access, user, isAuthenticated } = input;
  if (!pending) return 'none';
  if (!access.isAccessResolved) return 'wait';
  if (!access.isPaywallEnabled) return 'drop';
  if (!isAuthenticated || !user) return 'wait';
  if (!user.emailVerified) return 'wait';
  if (user.has_subscription || paidSubscriptionOf(user)) return 'billing';
  return 'checkout';
}

/** Дата продления/окончания: «Oct 7, 2026» как в макете. */
export function formatBillingDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
}
